/**
 * Help content for EyeconX SMS Gateway — FAQs and support options.
 */

export const FAQ_CATEGORIES = [
    {
        id: 'dashboard',
        title: 'Dashboard',
        icon: 'bi-speedometer2',
        color: 'text-primary',
    },
    {
        id: 'messages',
        title: 'Messages',
        icon: 'bi-chat-left-text',
        color: 'text-success',
    },
    {
        id: 'notifications',
        title: 'Notifications',
        icon: 'bi-bell',
        color: 'text-warning',
    },
    {
        id: 'settings',
        title: 'Clinic Settings',
        icon: 'bi-gear',
        color: 'text-info',
    },
    {
        id: 'general',
        title: 'General',
        icon: 'bi-question-circle',
        color: 'text-secondary',
    },
];

export const FAQ_ITEMS = [
    {
        id: 'dashboard-overview',
        categoryId: 'dashboard',
        question: 'What does the SMS Dashboard show?',
        answer:
            'The Dashboard opens with a header bar (like Messages and Settings) and a Refresh button. Below that, colorful overview cards summarize the last 30 days: total messages, delivered, inbox, patients contacted, failed, in-process, and confirmation messages. Daily and monthly charts sit side by side. At the bottom, a Failed messages section lets you review, resend, or delete undelivered texts.',
    },
    {
        id: 'dashboard-overview-cards',
        categoryId: 'dashboard',
        question: 'What do the colored overview cards mean?',
        answer:
            'Each card uses a gradient style and shows a key metric. Total Messages breaks down sent vs received. Delivered and Failed include success and failure rates. Inbox shows received count and unread badge when applicable. Patients shows unique contacts and read rate. In Process counts messages still queued or accepted. Confirmations counts appointment-confirmation sends.',
    },
    {
        id: 'dashboard-period',
        categoryId: 'dashboard',
        question: 'What time period does the dashboard cover?',
        answer:
            'Overview stats and the daily chart reflect the last 30 days. The monthly chart shows the last six months of sent, received, and failed volume. Use Refresh on the dashboard to pull updated numbers after sending messages or resending failures.',
    },
    {
        id: 'dashboard-cards-click',
        categoryId: 'dashboard',
        question: 'Can I click the overview cards?',
        answer:
            'Yes. Click the Inbox card to open Messages with Unread First sorting so you can prioritize unread conversations. Click the Failed card to scroll down to the Failed messages section. Other cards are informational only.',
    },
    {
        id: 'dashboard-confirmations',
        categoryId: 'dashboard',
        question: 'What does the Confirmations card mean?',
        answer:
            'This count shows outgoing messages sent as appointment confirmation requests—typically from automated workflows or API integrations rather than manual replies in Messages. Messages you send from the Messages inbox are regular two-way texts and are not included here.',
    },
    {
        id: 'dashboard-failed-list',
        categoryId: 'dashboard',
        question: 'How do I manage failed messages on the dashboard?',
        answer:
            'Scroll to the Failed messages card at the bottom (or click the Failed overview card). Each entry shows a round avatar with the patient\'s first and last name initials (styled like the Messages contact list), full name, formatted phone number, message preview, status badge, and time. Use the checkboxes to select one or more items, or Select all to choose every loaded entry. Tap Message to open that conversation, Resend to queue another send, or Delete to remove selected failed messages from the list. Use Refresh on that section or the dashboard header to update the list. Load more if needed before selecting additional items.',
    },
    {
        id: 'dashboard-failed-delete',
        categoryId: 'dashboard',
        question: 'How do I select and delete failed messages?',
        answer:
            'In the Failed messages section, use the checkbox on each row to select individual messages, or use Select all to select every entry currently loaded in the list. The Delete button in the section header shows how many items are selected (for example, Delete (2)). Click Delete, confirm in the dialog, and only the selected failed messages are removed. Select all applies to loaded items only—scroll down and click Load more first if you need to include older failures. Deleting removes entries from your failed messages list; it does not send a message to the patient. Use Resend if you want to try sending again instead.',
    },
    {
        id: 'dashboard-resend',
        categoryId: 'dashboard',
        question: 'What happens when I resend a failed message?',
        answer:
            'Resend shows a confirmation dialog, then queues the message to send again through SMS Gateway. The item may leave the failed list once it is accepted for resend. If resend errors, verify the patient number and that your clinic SMS line is set up under Settings. Refresh the dashboard to update overview counts.',
    },
    {
        id: 'dashboard-unread',
        categoryId: 'dashboard',
        question: 'Why does the unread count on the dashboard differ from Messages?',
        answer:
            'The dashboard loads when you open it and updates the global unread badge. The Messages inbox refreshes when you visit that page or use Refresh. If counts look out of sync, click Refresh on the dashboard, open Messages and refresh the inbox, or click the Inbox card to jump to Messages sorted by unread.',
    },
    {
        id: 'dashboard-charts',
        categoryId: 'dashboard',
        question: 'What are the daily and monthly charts?',
        answer:
            'The Daily Message Activity chart (left) plots sent and received volume over the last 30 days. Use the dropdowns to switch chart type (area, line, or bar) and time range (7, 14, or 30 days). The Monthly Message chart (right) shows sent, received, and failed totals for the last six months. Hover either chart for exact counts.',
    },
    {
        id: 'dashboard-failed-status',
        categoryId: 'dashboard',
        question: 'What do Failed and Undelivered badges mean?',
        answer:
            'In the Failed messages list, a red Failed badge means the carrier or gateway could not send the message. A yellow Undelivered badge means the message was sent but not confirmed as delivered to the handset. Both appear in the failed section so you can retry or follow up with the patient.',
    },
    {
        id: 'messages-inbox',
        categoryId: 'messages',
        question: 'How do I view and reply to patient messages?',
        answer:
            'Go to Messages, select a contact from the list on the left, and the conversation opens on the right. Type your reply in the message box and send. Outgoing messages are sent as regular two-way texts and appear in the thread with your clinic\'s SMS number as the sender. If your clinic has legacy two-way texting enabled, a Compose button appears next to Refresh so you can start a new message to a patient who is not already in your inbox.',
    },
    {
        id: 'messages-compose',
        categoryId: 'messages',
        question: 'What is the Compose button?',
        answer:
            'Compose appears to the left of Refresh on the Messages page when your clinic is configured for legacy two-way texting. Click Compose to search legacy EyeconX patients and start a new conversation. Select a patient from the results to open the message panel, type your text, and send. After sending, the contact list refreshes and the patient name appears in your inbox when available.',
    },
    {
        id: 'messages-legacy-search',
        categoryId: 'messages',
        question: 'How do I search for a patient in Compose?',
        answer:
            'In the Compose window, enter a search term and click Search (or press Enter). You can search by first name, last name, full name (for example, Aaron Phillips), or phone number (cell or home). Results load in a scrollable list—scroll down to load more patients automatically. Click a patient to open their conversation. Use the X button next to Search to close the Compose window.',
    },
    {
        id: 'messages-search',
        categoryId: 'messages',
        question: 'How can I search the inbox?',
        answer:
            'Use the search bar above the contact list on the Messages page (not inside Compose). Choose a search type: First Name, Last Name, Phone, or Date (calendar picker). Enter your term and press Enter or click the search button. Use the X button to clear search and show the full inbox again. Compose search is separate and is used only when starting a new legacy patient conversation.',
    },
    {
        id: 'messages-sort',
        categoryId: 'messages',
        question: 'How do I sort conversations?',
        answer:
            'Click the sort icon next to the search bar. Options include Newest First, Oldest First, and Unread First so you can prioritize contacts who have not been read yet.',
    },
    {
        id: 'messages-load-more',
        categoryId: 'messages',
        question: 'Why do I only see some contacts at first?',
        answer:
            'The inbox loads in pages for performance. Scroll to the bottom of the contact list and more conversations load automatically as you scroll. Searching or changing sort order reloads the list from the beginning. In the Compose patient search window, scroll the results list to load additional matching patients the same way.',
    },
    {
        id: 'messages-compose-name',
        categoryId: 'messages',
        question: 'Why does a new Compose contact show only a phone number?',
        answer:
            'The inbox shows the patient name after your first message is sent with name details from the legacy patient record. If you still see only a number, the legacy record may be missing a name or the patient may belong to a different legacy clinic mapping—try searching again in Compose or contact your administrator to verify legacy two-way texting and clinic setup.',
    },
    {
        id: 'messages-details',
        categoryId: 'messages',
        question: 'How do I see patient details in a conversation?',
        answer:
            'Open a conversation and use the patient or details control in the conversation header (where available) to load conversation details from the server, such as linked patient information tied to that phone number.',
    },
    {
        id: 'messages-delete',
        categoryId: 'messages',
        question: 'Can I delete a conversation?',
        answer:
            'Yes. With a conversation selected, use the delete option in the conversation panel. This removes the thread from your SMS Gateway inbox view. Confirm before deleting, as this action is intended to clear the conversation from your gateway list.',
    },
    {
        id: 'messages-failed',
        categoryId: 'messages',
        question: 'Why did my message fail to send?',
        answer:
            'Failures can occur if the patient number is invalid, the carrier rejected the message, or your clinic SMS line is not properly configured. On the Dashboard, check the Failed overview card and use the Failed messages list to resend, delete, or open the conversation. Ensure your clinic SMS number is verified under Settings, and contact support if failures persist.',
    },
    {
        id: 'notifications-realtime',
        categoryId: 'notifications',
        question: 'How do real-time notifications work?',
        answer:
            'When a new SMS or alert arrives for your clinic, the app can play a sound and increase the bell badge in the top navigation. Notifications are tied to your clinic ID on the notification server. Real-time alerts may be disabled in local development environments.',
    },
    {
        id: 'notifications-past',
        categoryId: 'notifications',
        question: 'What is Past Notifications?',
        answer:
            'Open Past Notifications from Quick Links on the Settings page. It stores alerts received on this browser and device in local storage. You can review message previews, see type labels (such as incoming SMS), delete individual items, or clear all. Use Back to Settings in the header to return. This history is per device—it does not replace server-side message records in Messages.',
    },
    {
        id: 'notifications-quick-reply',
        categoryId: 'notifications',
        question: 'How does Quick Reply from a notification work?',
        answer:
            'From Past Notifications, use Quick Reply on an item that includes a patient phone number. You will be taken to Messages with that conversation opened so you can respond immediately.',
    },
    {
        id: 'notifications-bell',
        categoryId: 'notifications',
        question: 'What happens when I click the bell icon?',
        answer:
            'Clicking the bell resets the notification badge counter and navigates you to Messages so you can handle unread conversations.',
    },
    {
        id: 'settings-view',
        categoryId: 'settings',
        question: 'What can I view under Clinic Settings?',
        answer:
            'The Settings page has a header bar (like Dashboard and Messages) with your clinic profile on the left and Quick Links plus Clinic Hours on the right. You can view clinic name, ID, contact details (email, phone, SMS number), signature, website, timezone, full address, verification status (email, SMS, phone), clinic logo, and clinic hours. Most SMS and ID fields are read-only; editable fields are available in Edit mode.',
    },
    {
        id: 'settings-edit',
        categoryId: 'settings',
        question: 'How do I update clinic information?',
        answer:
            'On the Clinic Settings page, click Edit, change allowed fields (name, email, phone, signature, website, address), then Save Changes. You will be asked to confirm before changes are sent. SMS number and clinic type cannot be changed from this screen.',
    },
    {
        id: 'settings-verification',
        categoryId: 'settings',
        question: 'Why did verification status show "No" after I saved?',
        answer:
            'Verification flags are managed separately from basic clinic info. Saving your profile does not remove verification in the database. If status ever looks wrong after an update, refresh the page—the app reloads full clinic data including verification after a successful save.',
    },
    {
        id: 'settings-hours',
        categoryId: 'settings',
        question: 'Can I change clinic hours here?',
        answer:
            'Clinic hours are displayed for reference on the Settings page. To change hours, use your main EyeconX clinic administration workflow or contact your account administrator if hours are managed outside SMS Gateway.',
    },
    {
        id: 'settings-quick-links',
        categoryId: 'settings',
        question: 'What are Quick Links on the Settings page?',
        answer:
            'Quick Links on the right side of Clinic Settings give shortcuts to Change Password and Past Notifications. Change Password opens a form to update your login password (you will be signed out after a successful change). Past Notifications shows alerts stored on this browser for your clinic.',
    },
    {
        id: 'settings-password',
        categoryId: 'settings',
        question: 'How do I change my password?',
        answer:
            'Go to Clinic Settings and click Change Password under Quick Links, or open Help & Support and use the Change Password shortcut under Get Support. Enter your current password, then your new password twice. Password must be at least 8 characters with uppercase, lowercase, and a number. After a successful change you are logged out automatically for security—sign in again with your new password.',
    },
    {
        id: 'settings-help',
        categoryId: 'settings',
        question: 'Where can I find Help & Support?',
        answer:
            'Click Help & Support at the bottom of the left sidebar (above Logout). You can browse FAQs by category, search by keyword, or switch to Get Support for email contact and quick links to Dashboard, Messages, Settings, and more.',
    },
    {
        id: 'general-legacy-compose',
        categoryId: 'general',
        question: 'Why do I not see the Compose button?',
        answer:
            'Compose is shown only when your clinic has legacy two-way texting enabled in EyeconX customization settings. If you do not see Compose next to Refresh on Messages, your clinic may use standard SMS Gateway inbox only, or the setting has not been turned on yet. Contact your EyeconX administrator to enable legacy two-way texting if you need to message legacy EyeconX patients who are not already in your inbox.',
    },
    {
        id: 'general-login',
        categoryId: 'general',
        question: 'Who can access SMS Gateway?',
        answer:
            'Users at clinics configured for SMS Gateway (clinic type SMS_GATEWAY) sign in with their EyeconX credentials. Roles such as Manager, ECP, and Staff can use dashboard, messages, and settings according to your organization\'s permissions.',
    },
    {
        id: 'general-sms-number',
        categoryId: 'general',
        question: 'Which number do patients see when I text them?',
        answer:
            'Outgoing messages use your clinic\'s registered SMS business number shown on the Settings page. Patients reply to that number, and replies appear in your Messages inbox.',
    },
    {
        id: 'general-logout',
        categoryId: 'general',
        question: 'How do I sign out?',
        answer:
            'Use Logout in the top navigation or at the bottom of the left sidebar. This clears your session and returns you to the login page. Sign in again with your clinic credentials to continue.',
    },
];

export const LEGACY_COMPOSE_FAQ_IDS = new Set([
    'messages-compose',
    'messages-legacy-search',
    'messages-compose-name',
    'general-legacy-compose',
]);

const STANDARD_FAQ_ANSWERS = {
    'messages-inbox':
        'Go to Messages, select a contact from the list on the left, and the conversation opens on the right. Type your reply in the message box and send. Outgoing messages are sent as regular two-way texts and appear in the thread with your clinic\'s SMS number as the sender.',
    'messages-search':
        'Use the search bar above the contact list. Choose a search type: First Name, Last Name, Phone, or Date (calendar picker). Enter your term and press Enter or click the search button. Use the X button to clear search and show the full inbox again.',
    'messages-load-more':
        'The inbox loads in pages for performance. Scroll to the bottom of the contact list and more conversations load automatically as you scroll. Searching or changing sort order reloads the list from the beginning.',
};

export function getFaqItems(isLegacyTwoWayText) {
    return FAQ_ITEMS
        .filter((item) => isLegacyTwoWayText || !LEGACY_COMPOSE_FAQ_IDS.has(item.id))
        .map((item) => {
            if (isLegacyTwoWayText || !STANDARD_FAQ_ANSWERS[item.id]) {
                return item;
            }

            return {
                ...item,
                answer: STANDARD_FAQ_ANSWERS[item.id],
            };
        });
}

const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || 'support@eyeconx.com';

export const SUPPORT_OPTIONS = [
    {
        id: 'email',
        title: 'Email Support',
        description: 'Send questions about SMS Gateway, delivery issues, or account access.',
        icon: 'bi-envelope-fill',
        color: 'primary',
        actionLabel: 'Email support',
        href: `mailto:${SUPPORT_EMAIL}?subject=EyeconX%20SMS%20Gateway%20Support`,
        external: true,
    },
    {
        id: 'dashboard',
        title: 'SMS Dashboard',
        description: 'Overview cards, daily and monthly charts, and failed messages with resend and delete.',
        icon: 'bi-speedometer2',
        color: 'primary',
        actionLabel: 'Go to Dashboard',
        to: '/sms-dashboard',
    },
    {
        id: 'messages',
        title: 'Messages inbox',
        description: 'Reply to patients, search your inbox, or use Compose (legacy clinics) to start new conversations.',
        icon: 'bi-chat-left-text-fill',
        color: 'success',
        actionLabel: 'Go to Messages',
        to: '/sms-messages',
    },
    {
        id: 'settings',
        title: 'Clinic Settings',
        description: 'Check your SMS number, verification status, clinic hours, and Quick Links.',
        icon: 'bi-gear-fill',
        color: 'info',
        actionLabel: 'Go to Settings',
        to: '/sms-clinic-settings',
    },
    {
        id: 'password',
        title: 'Change Password',
        description: 'Update your account password. You will be signed out after a successful change.',
        icon: 'bi-key-fill',
        color: 'secondary',
        actionLabel: 'Change password',
        to: '/sms-change-password',
    },
    {
        id: 'notifications',
        title: 'Past Notifications',
        description: 'Review alerts received on this device and quick-reply to patients.',
        icon: 'bi-bell-fill',
        color: 'warning',
        actionLabel: 'View notifications',
        to: '/sms-notifications',
    },
];

export function getSupportOptions(isLegacyTwoWayText) {
    return SUPPORT_OPTIONS.map((option) => {
        if (option.id === 'messages' && !isLegacyTwoWayText) {
            return {
                ...option,
                description: 'Open your patient conversations to reply or search for a contact.',
            };
        }

        return option;
    });
}
